import { assign, cloneDeep, debounce, uniq } from 'lodash';

import { Logger } from '@mode/shared/contract-common';
import { DATA_LIMIT } from '../../../../common/constants';
import { PivotTableViewOptions } from '../../../../common/types';
import { assignWithDefined } from '../../../../lib/axel/util';
import {
  CONTEXT_LIST_TYPE_MULTIPLE_INVERT,
  CONTEXT_LIST_TYPE_SINGLE,
} from '../settings-context-panel/settings-context-panel-controller';

export default class PivotTableSettingsController {
  static $inject = ['$scope', '$element', '$timeout', '$log', '$state'];

  _view: any;
  view: any;
  data: any;
  token: string;
  containerHeight: number;
  columnView: any;
  pivotConfigIntro = ``;

  state = {
    accordion: {
      chart: true,
      format: false,
    },
    contextPanel: {
      quadrant: '',
      field: '',
      enabled: false,
      filterEnabled: false,
      available: [],
      selected: [],
    },
  };

  onSettingsUpdate: (opts: { $token: string; $view: any }) => void;

  constructor(
    private $scope: angular.IScope,
    private $element,
    private $timeout,
    private $log: Logger,
    private $state
  ) {}

  $onInit() {
    this.initView();
    if (typeof this.onSettingsUpdate === 'function') {
      this.onSettingsUpdate = debounce(this.onSettingsUpdate, 150);
    }
  }

  $onChanges(changed) {
    if (!this.dataExceedsLimit()) {
      if (changed.view) {
        this.initView();
        this.onSelectedColumnChanged();
      }
    }
  }

  initView() {
    this._view = cloneDeep(this.view || {});
    if (this.view.columnFormats == null) {
      this.view.columnFormats = {};
    }
  }

  dataExceedsLimit() {
    return this.data.count >= DATA_LIMIT;
  }

  private onSelectedColumnChanged() {
    this.columnView = this.view.columnFormats[this.view.values && this.view.values[0]] || {};
  }

  isReportMode() {
    return !this.$state.includes('application.reports.edit');
  }

  getPivotConfigHeight() {
    return this.containerHeight - this.$element.find('.chart-nav').outerHeight();
  }
  /**
   * Update local options and pass to update callback
   *
   * @param {object} opts
   * @param {PivotTableViewOptions} opts.view
   */
  updateSettings(opts: { view: PivotTableViewOptions }) {
    const { view } = opts;
    this.onSettingsUpdate({ $token: this.token, $view: view });
  }

  handleItemContextTrigger($quadrant, $itemId) {
    this.openQuadrantContextPanel($quadrant, $itemId);
  }

  openQuadrantContextPanel($quadrant, $itemId) {
    const contextStates = {
      filters: {
        quadrant: $quadrant,
        field: $itemId,
        type: CONTEXT_LIST_TYPE_MULTIPLE_INVERT,
        available: uniq(this.data.content.map((d) => d[$itemId])),
        selected: this.view.filterValues[$itemId] || [],
        enabled: true,
        filterEnabled: true,
      },
      values: {
        quadrant: $quadrant,
        field: $itemId,
        type: CONTEXT_LIST_TYPE_SINGLE,
        available: ['sum', 'count', { value: 'countDistinct', displayName: 'count distinct' }, 'average', 'max', 'min'],
        selected: [this.view.aggregator],
        enabled: true,
        filterEnabled: false,
      },
    };

    this.$timeout(() => {
      assignWithDefined(this.state.contextPanel, contextStates[$quadrant]);
    });
  }

  handleContextDismiss() {
    this.state.contextPanel.enabled = false;
  }

  /**
   * @param $selected
   * @param $context
   */
  handleContextUpdate($selected, $context) {
    const view = this.pivotTableViewOptions();
    const field = this.state.contextPanel.field;
    const contextPropertyMap = {
      filters: 'filterValues',
      values: 'values',
    };
    const contextUpdateMap = {
      values: (value) => this.updateSettingsProperty('aggregator', value[0]),
      filters: (value) => {
        const key = `${contextPropertyMap[$context]}`;
        const old = view[key];
        const update = { [`${field}`]: value };

        return this.updateSettingsDeep({ [key]: assign({}, old, update) });
      },
    };

    contextUpdateMap[$context]($selected);
  }

  handleFilterChange($field, $old) {
    const view = cloneDeep(this.pivotTableViewOptions());
    const removedFilters = $old.filters.filter((f) => $field.indexOf(f) === -1);
    const addedFilters = $field.filter((f) => $old.filters.indexOf(f) === -1);

    // update mapped filters
    view.filters = $field;

    // clear filterValues for removed filters
    removedFilters.forEach((f) => (view.filterValues[f] = []));
    this.$scope.$evalAsync(() => this.updateSettings({ view }));

    // open context panel when filter added
    if (addedFilters.length) {
      this.openQuadrantContextPanel('filters', addedFilters[0]);
    }
  }

  handleValueChange($field, $old) {
    this.$scope.$evalAsync(() => this.updateSettingsProperty('values', $field));
    this.onSelectedColumnChanged();
  }

  handleColumnChange($field, $old) {
    this.$scope.$evalAsync(() => this.updateSettingsProperty('columns', $field));
  }

  handleRowChange($field, $old) {
    this.$scope.$evalAsync(() => this.updateSettingsProperty('rows', $field));
  }

  updateSettingsDeep(values) {
    const view = cloneDeep(this.pivotTableViewOptions());

    assign(view, values);
    this.updateSettings({ view });
  }

  /**
   * @param property
   * @param value
   */
  updateSettingsProperty(property, value) {
    const view = cloneDeep(this.pivotTableViewOptions());
    if (!view.hasOwnProperty(property)) {
      this.$log.error(new Error('Invalid property "' + property + '"'));
    }

    view[property] = value;
    this.updateSettings({ view });
  }

  getFormatSettings() {
    return this.columnView;
  }

  pivotTableViewOptions(): PivotTableViewOptions {
    return this._view;
  }

  selectTab(name: string) {
    const accordion = this.state.accordion;
    const setAccordion = (name: string, state: boolean) => (accordion[name] = state);
    const curriedTabSelect = (selected: string) => (section: string) => setAccordion(section, selected === section);

    if (accordion.hasOwnProperty(name)) {
      Object.keys(accordion).forEach(curriedTabSelect(name));
    }
  }

  private updateFormatSettings($formatOptions) {
    const currValueColumn = this.view.values[0];

    this.updateSettingsDeep({
      columnFormats: assign({}, this.view.columnFormats, {
        [`${currValueColumn}`]: $formatOptions,
      }),
    });
  }
}
